import { ensureFreshToken } from '../utils/tokenRefresh';

/**
 * Applications connectées (contrat Tindisa §1) : examen, autorisation, refus
 * et révocation des liens marchand. Ces routes vivent dans la gestion
 * commerciale (`/commerce/api/v1/partner-links`), derrière le même portail que
 * Wanzo Land ; l'adresse se déduit donc de VITE_API_URL.
 */
const LAND_API = import.meta.env.VITE_API_URL || 'http://localhost:8000/land/api/v1';
const COMMERCE_API: string =
  import.meta.env.VITE_COMMERCE_API_URL || LAND_API.replace(/\/land\/api\/v1\/?$/, '/commerce/api/v1');

export type PartnerScope = 'catalog.read' | 'stock.read' | 'orders.write' | 'customers.write';

export interface PartnerLinkRequest {
  linkId: string;
  clientName: string;
  displayName: string;
  partnerSubject: string;
  requestedScopes: PartnerScope[];
  expiresAt: string;
  businessUnits: Array<{ id: string; name: string; type: string; code?: string }>;
  displayCurrencies: string[];
}

export interface ConnectedApp {
  id: string;
  clientName: string;
  displayName: string;
  partnerSubject: string;
  status: 'active' | 'revoked' | 'pending' | 'refused' | 'expired';
  scopes: PartnerScope[];
  businessUnitId: string | null;
  businessUnitName: string | null;
  displayCurrency: string | null;
  /** Remise maximale que l’application peut consentir (%), plancher de négociation. */
  maxDiscountPct: number;
  approvedAt: string | null;
  revokedAt: string | null;
  revokeReason: string | null;
}

/** Libellés lisibles des autorisations demandées. */
export const SCOPE_LABELS: Record<PartnerScope, { title: string; detail: string }> = {
  'catalog.read': {
    title: 'Lire votre catalogue',
    detail: 'Articles, services et plats marqués visibles en ligne, avec leurs prix de vente et photos. Jamais vos prix d’achat.',
  },
  'stock.read': {
    title: 'Consulter vos stocks',
    detail: 'Quantités disponibles des articles publiés, par emplacement.',
  },
  'orders.write': {
    title: 'Enregistrer des ventes',
    detail: 'Les commandes reçues deviennent des ventes Wanzo, au prix de votre catalogue.',
  },
  'customers.write': {
    title: 'Ajouter des clients',
    detail: 'Les acheteurs sont ajoutés à votre fichier clients, sans modifier les fiches existantes.',
  },
};

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await ensureFreshToken();
  let response: Response;
  try {
    response = await fetch(`${COMMERCE_API}${path}`, {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
  } catch {
    throw new Error('Connexion impossible. Vérifiez votre réseau puis réessayez.');
  }
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }
  const envelope: { success?: unknown; message?: unknown } =
    payload !== null && typeof payload === 'object' ? payload : {};
  if (!response.ok || envelope.success === false) {
    const message = typeof envelope.message === 'string' ? envelope.message : '';
    if (response.status === 401) throw new Error('Votre session a expiré. Reconnectez-vous.');
    if (response.status === 403) throw new Error(message || 'Action réservée au propriétaire ou à un administrateur.');
    if (response.status === 429) throw new Error('Trop de tentatives. Patientez une minute.');
    throw new Error(message && !/^(Internal|An error)/i.test(message) ? message : 'Une erreur est survenue. Réessayez.');
  }
  // La gestion commerciale enveloppe ses réponses : { success, data }.
  return (payload && typeof payload === 'object' && 'data' in payload ? payload.data : payload) as T;
}

export const partnerLinksApi = {
  list: () => call<ConnectedApp[]>('/partner-links'),
  preview: (code: string) => call<PartnerLinkRequest>(`/partner-links/request?code=${encodeURIComponent(code)}`),
  approve: (body: { code: string; businessUnitId?: string; displayCurrency: string; scopes: PartnerScope[]; maxDiscountPct: number }) =>
    call<ConnectedApp>('/partner-links/approve', { method: 'POST', body: JSON.stringify(body) }),
  refuse: (code: string) =>
    call<{ linkId: string; status: string }>('/partner-links/refuse', { method: 'POST', body: JSON.stringify({ code }) }),
  update: (id: string, body: { maxDiscountPct: number }) =>
    call<ConnectedApp>(`/partner-links/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  revoke: (id: string) => call<{ linkId: string; status: string }>(`/partner-links/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

/** Code saisi : 8 caractères, sans espaces ni tirets. */
export function normalizeCode(raw: string): string {
  return String(raw || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
}

/** Remise saisie : 0 à 90 %, deux décimales ; null si invalide. */
export function parseDiscount(raw: string): number | null {
  const n = Number(String(raw ?? '').replace(',', '.').trim() || '0');
  if (!Number.isFinite(n) || n < 0 || n > 90) return null;
  return Math.round(n * 100) / 100;
}
